import { useState } from "react";
import { Eye, EyeOff, Loader2, ArrowRightLeft, ShieldCheck, CheckCircle2, XCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const Label = ({ children, htmlFor }) => (
  <label
    htmlFor={htmlFor}
    className="block uppercase tracking-wider text-xs text-zinc-400 font-semibold mb-1.5"
  >
    {children}
  </label>
);

const FieldError = ({ error, testId }) =>
  error ? (
    <p data-testid={testId} className="mt-1.5 text-xs text-red-400 font-medium">
      {error}
    </p>
  ) : null;

const inputBase =
  "bg-zinc-950 border-zinc-800 text-zinc-100 placeholder:text-zinc-600 focus-visible:ring-2 focus-visible:ring-emerald-500/30 focus-visible:border-emerald-500 h-11 font-mono";

const PreflightRow = ({ label, info, testId }) => {
  if (!info) return null;
  const ok = info.reachable;
  return (
    <div data-testid={testId} className="flex items-start gap-2 text-xs">
      {ok ? (
        <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
      ) : (
        <XCircle className="h-4 w-4 text-red-400 shrink-0 mt-0.5" />
      )}
      <div className="min-w-0">
        <span className="text-zinc-400">{label}: </span>
        {ok ? (
          <span className="font-mono text-emerald-300">
            reachable{info.name ? ` — ${info.name}` : ""}
          </span>
        ) : (
          <span className="font-mono text-red-300 break-words">
            {info.error?.message || `not reachable (HTTP ${info.http_status || "—"})`}
          </span>
        )}
      </div>
    </div>
  );
};

export const MigrationForm = ({ values, errors, onChange, onSubmit, loading, onVerify, verifying, preflight }) => {
  const [showToken, setShowToken] = useState(false);
  const busy = loading || verifying;

  return (
    <form
      data-testid="migration-form"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className="space-y-5"
    >
      <div>
        <Label htmlFor="sourceWabaId">Source WABA ID</Label>
        <Input
          id="sourceWabaId"
          data-testid="source-waba-input"
          className={inputBase}
          placeholder="Enter Source WABA ID"
          value={values.sourceWabaId}
          onChange={(e) => onChange("sourceWabaId", e.target.value)}
          disabled={busy}
          autoComplete="off"
          inputMode="numeric"
        />
        <FieldError error={errors.sourceWabaId} testId="source-waba-error" />
      </div>

      <div className="flex justify-center -my-1">
        <div className="rounded-full border border-zinc-800 bg-zinc-900 p-1.5 text-emerald-400">
          <ArrowRightLeft className="h-3.5 w-3.5" />
        </div>
      </div>

      <div>
        <Label htmlFor="destinationWabaId">Destination WABA ID</Label>
        <Input
          id="destinationWabaId"
          data-testid="destination-waba-input"
          className={inputBase}
          placeholder="Enter Destination WABA ID"
          value={values.destinationWabaId}
          onChange={(e) => onChange("destinationWabaId", e.target.value)}
          disabled={busy}
          autoComplete="off"
          inputMode="numeric"
        />
        <FieldError error={errors.destinationWabaId} testId="destination-waba-error" />
      </div>

      <div>
        <Label htmlFor="accessToken">Meta Access Token</Label>
        <div className="relative">
          <Input
            id="accessToken"
            data-testid="access-token-input"
            type={showToken ? "text" : "password"}
            className={`${inputBase} pr-11`}
            placeholder="Enter Meta Access Token"
            value={values.accessToken}
            onChange={(e) => onChange("accessToken", e.target.value)}
            disabled={busy}
            autoComplete="off"
          />
          <button
            type="button"
            data-testid="show-password-toggle"
            onClick={() => setShowToken((s) => !s)}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-zinc-500 hover:text-emerald-400 hover:bg-zinc-800 transition-colors"
            tabIndex={-1}
            aria-label={showToken ? "Hide token" : "Show token"}
          >
            {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        <FieldError error={errors.accessToken} testId="access-token-error" />
        <p className="mt-1.5 text-[11px] text-zinc-500 leading-relaxed">
          Your token is used only for this request and is never stored, logged, or exported.
        </p>
      </div>

      <div>
        <Label htmlFor="apiVersion">API Version (optional)</Label>
        <Input
          id="apiVersion"
          data-testid="api-version-input"
          className={inputBase}
          placeholder="v22.0"
          value={values.apiVersion}
          onChange={(e) => onChange("apiVersion", e.target.value)}
          disabled={busy}
          autoComplete="off"
        />
        <p className="mt-1.5 text-[11px] text-zinc-500">Defaults to the latest supported Graph API version.</p>
      </div>

      <Button
        type="submit"
        data-testid="migrate-submit-button"
        disabled={busy}
        className="w-full h-14 text-base font-bold text-white bg-gradient-to-r from-blue-600 to-emerald-500 hover:from-blue-500 hover:to-emerald-400 disabled:opacity-80 disabled:cursor-not-allowed transition-[filter,opacity] shadow-lg shadow-emerald-900/20"
      >
        {loading ? (
          <span className="flex items-center gap-2">
            <Loader2 className="h-5 w-5 animate-spin" />
            Migrating templates...
          </span>
        ) : (
          "MIGRATE TEMPLATES"
        )}
      </Button>
      {loading && (
        <p data-testid="loading-message" className="text-center text-xs text-zinc-400">
          Please wait for Meta's response.
        </p>
      )}

      <Button
        type="button"
        data-testid="verify-access-button"
        onClick={onVerify}
        disabled={busy}
        variant="outline"
        className="w-full h-11 border-zinc-700 bg-zinc-950 text-zinc-200 hover:bg-zinc-800 hover:text-white disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {verifying ? (
          <span className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            Verifying access...
          </span>
        ) : (
          <span className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4" />
            Verify Access (optional)
          </span>
        )}
      </Button>

      {preflight && (
        <div
          data-testid="preflight-results"
          className="rounded-md border border-zinc-800 bg-zinc-950/60 p-3 space-y-2"
        >
          <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Access Check</p>
          <PreflightRow label="Source WABA" info={preflight.source} testId="preflight-source" />
          <PreflightRow label="Destination WABA" info={preflight.destination} testId="preflight-destination" />
        </div>
      )}
    </form>
  );
};
